/**
 * Small word-level text diff for showing AI proofreading changes inline:
 * deleted text (red, strikethrough) and inserted text (green). Tokens are
 * words, whitespace runs and single punctuation marks, so a changed comma
 * or a corrected word is highlighted on its own while the rest stays equal.
 */

const TOKEN_RE = /\s+|[A-Za-zÀ-ÖØ-öø-ÿ0-9_]+|[^\sA-Za-zÀ-ÖØ-öø-ÿ0-9_]/g;
// Above this many DP cells we fall back to a line diff to keep memory bounded.
const MAX_CELLS = 2000000;

function tokenize(text) {
    return (text || '').match(TOKEN_RE) || [];
}

// Longest-common-subsequence alignment that walks FORWARD so a deletion is
// reported where the reader expects it (after the common prefix), not shifted
// to re-use a later identical token.
function lcsOps(a, b) {
    const n = a.length;
    const m = b.length;
    const width = m + 1;
    const table = new Uint16Array((n + 1) * width);
    for (let i = n - 1; i >= 0; i -= 1) {
        for (let j = m - 1; j >= 0; j -= 1) {
            if (a[i] === b[j]) {
                table[i * width + j] = table[(i + 1) * width + j + 1] + 1;
            } else {
                const down = table[(i + 1) * width + j];
                const right = table[i * width + j + 1];
                table[i * width + j] = down >= right ? down : right;
            }
        }
    }
    const ops = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (a[i] === b[j]) {
            ops.push({ type: 'equal', text: a[i] });
            i += 1;
            j += 1;
        } else if (table[(i + 1) * width + j] >= table[i * width + j + 1]) {
            ops.push({ type: 'del', text: a[i] });
            i += 1;
        } else {
            ops.push({ type: 'ins', text: b[j] });
            j += 1;
        }
    }
    while (i < n) { ops.push({ type: 'del', text: a[i] }); i += 1; }
    while (j < m) { ops.push({ type: 'ins', text: b[j] }); j += 1; }
    return ops;
}

// Merge adjacent ops of the same type. Deletions are emitted before
// insertions inside one change group so "old → new" reads naturally.
function mergeOps(ops) {
    const out = [];
    let pendingDel = '';
    let pendingIns = '';
    const flush = () => {
        if (pendingDel) out.push({ type: 'del', text: pendingDel });
        if (pendingIns) out.push({ type: 'ins', text: pendingIns });
        pendingDel = '';
        pendingIns = '';
    };
    ops.forEach((op) => {
        if (op.type === 'equal') {
            flush();
            if (out.length && out[out.length - 1].type === 'equal') {
                out[out.length - 1] = { type: 'equal', text: out[out.length - 1].text + op.text };
            } else {
                out.push({ type: 'equal', text: op.text });
            }
        } else if (op.type === 'del') {
            pendingDel += op.text;
        } else {
            pendingIns += op.text;
        }
    });
    flush();
    return out;
}

function splitLinesKeep(text) {
    return (text || '').split(/(\n)/).filter(t => t !== '');
}

export function diffWords(oldText = '', newText = '') {
    if (oldText === newText) {
        return oldText ? [{ type: 'equal', text: oldText }] : [];
    }
    let a = tokenize(oldText);
    let b = tokenize(newText);
    if ((a.length + 1) * (b.length + 1) > MAX_CELLS) {
        a = splitLinesKeep(oldText);
        b = splitLinesKeep(newText);
    }
    return mergeOps(lcsOps(a, b));
}

export function countChanges(segments = []) {
    let count = 0;
    let inChange = false;
    segments.forEach((s) => {
        if (s.type === 'equal') {
            inChange = false;
        } else if (!inChange) {
            inChange = true;
            count += 1;
        }
    });
    return count;
}
